// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { Redirect, withRouter, Switch } from 'react-router-dom';
import styled from '@emotion/styled';
import { get } from 'lodash/fp';
import { parse as parseQuery } from 'qs';

import Page from 'app/containers/parts/Page';
import { rewritePath } from 'app/utils/routeUtils';
import UserOnly from 'app/components/util/UserOnly';
import { setLobbySport } from '../actions/lobbyActions';
import SportsFilterBox from '../components/sportsFilters/SportsFilterBox';
import MyTeamsMenu from './myTeams/MyTeamsMenu';
import PlayerSwap from './myTeams/PlayerSwap';
import MyLiveTeamsContent from './myTeams/MyLiveTeamsContent';
import MyUpcomingTeamsContent from './myTeams/MyUpcomingTeamsContent';
import UploadLineup from './myTeams/UploadLineup';
import EditLineup from './myTeams/EditLineup';
import BatchEditLineup from './myTeams/BatchEditLineup';

type Props = {
  activeSport?: string,
  enableUploadLineup?: boolean,
  lineupId?: string,
  history: Object,
  onSportSelect: (sport: string, subCategory?: string) => void,
  rewriteRules: Object,
  section?: string,
  showSportTabs: boolean,
  title: string
};

const Content = styled.div({
  padding: '0.5em 0'
});

const titles = {
  'upload-lineup': 'Upload Lineup',
  'edit-lineup': 'Edit Lineup',
  'batch-edit': 'Batch Edit Entries'
};

const defaultProps = {
  activeSport: undefined,
  enableUploadLineup: false,
  lineupId: undefined,
  section: undefined
};

function MyTeams(props: Props) {
  const {
    activeSport,
    enableUploadLineup,
    lineupId,
    onSportSelect,
    section,
    showSportTabs,
    title,
    history,
    rewriteRules
  } = props;

  const handleShowBatchEdit = () => {
    history.push('/my-teams/batch-edit');
  };

  const handleShowUpload = () => {
    history.push('/my-teams/upload-lineup');
  };

  const handleViewContest = (id: string, entryId: string) => {
    history.push(`${rewritePath(rewriteRules)('/challenge')}/${id}/${entryId}`);
  };

  const handleEditLineup = (id: number) => {
    history.push(`/my-teams/edit-lineup?id=${id}`);
  };

  const handleGoUpcomingTeams = () => {
    history.push('/my-teams/upcoming-teams');
  };

  return (
    <UserOnly>
      <Switch>
        <Redirect exact from="/my-teams" to="/my-teams/live-teams" />
      </Switch>
      <Page title={title}>
        {section && (
          <React.Fragment>
            {showSportTabs && (
              <SportsFilterBox
                activeSport={activeSport}
                namespace={`/my-teams/${section}`}
                onSportSelect={onSportSelect}
                showAll
                showSubCategories={false}
              />
            )}
            {showSportTabs && (
              <MyTeamsMenu
                activeSport={activeSport}
                onShowBatchEdit={handleShowBatchEdit}
                onShowUpload={handleShowUpload}
                section={section}
                uploadLineup={enableUploadLineup}
              />
            )}
            <Content>
              {section === 'swap-players' && <PlayerSwap />}
              {section === 'live-teams' && (
                <MyLiveTeamsContent activeSport={activeSport} onViewContest={handleViewContest} />
              )}
              {section === 'upcoming-teams' && (
                <MyUpcomingTeamsContent activeSport={activeSport} onEditLineup={handleEditLineup} />
              )}
              {section === 'upload-lineup' && <UploadLineup onRedirect={handleGoUpcomingTeams} />}
              {section === 'edit-lineup' && (
                <EditLineup lineupId={lineupId} onBack={handleGoUpcomingTeams} />
              )}
              {section === 'batch-edit' && <BatchEditLineup onRedirect={handleGoUpcomingTeams} />}
            </Content>
          </React.Fragment>
        )}
      </Page>
    </UserOnly>
  );
}

MyTeams.defaultProps = defaultProps;

function mapStateToProps(state: GlobalState, { match, location }) {
  const query = parseQuery(location.search, { ignoreQueryPrefix: true });

  return {
    activeSport: state.lobbyChallenges.activeSport || undefined,
    enableUploadLineup: !!get('settings.enableUploadLineup', state),
    lineupId: query.id,
    section: match.params.section,
    rewriteRules: state.settings.rewriteRules,
    showSportTabs: Object.keys(titles).includes(match.params.section) === false,
    title: get(match.params.section, titles) || 'My Teams'
  };
}

function mapDispatchToProps(dispatch: Dispatch<*>) {
  return {
    onSportSelect(sport: string, subCategory?: string) {
      dispatch(setLobbySport(sport, subCategory));
    }
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(MyTeams));
